import { useState, useMemo, useRef, useEffect, type ReactNode } from 'react';
import { Search, GraduationCap } from 'lucide-react';
import { Tutee } from '../../../types/tuition';
import { TUTEE_ICON_MAP } from '../../../utils/tuteeIcons';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = TUTEE_ICON_MAP;

interface AdminGroupSelectorProps {
  tutees: Tutee[];
  selectedTuteeId: string;
  onSelect: (tuteeId: string) => void;
  variant?: 'default' | 'sticky';
}

function ScrollFadeRow({
  scrollRef,
  fadeLeft,
  fadeRight,
  onScroll,
  fadeFromClass,
  children,
  scrollClassName,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  fadeLeft: boolean;
  fadeRight: boolean;
  onScroll: () => void;
  fadeFromClass: string;
  children: ReactNode;
  scrollClassName?: string;
}) {
  return (
    <div className="relative min-h-9">
      <div
        aria-hidden
        className={`pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-r ${fadeFromClass} to-transparent transition-opacity duration-200 ${
          fadeLeft ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute right-0 top-0 bottom-0 w-8 z-10 bg-gradient-to-l ${fadeFromClass} to-transparent transition-opacity duration-200 ${
          fadeRight ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <div
        ref={scrollRef}
        role="tablist"
        aria-label="Tuition group"
        onScroll={onScroll}
        className={`flex gap-2 w-full overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch] snap-x snap-proximity scroll-px-3 px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden py-0.5 ${scrollClassName ?? ''}`}
      >
        {children}
      </div>
    </div>
  );
}

const AdminGroupSelector = ({
  tutees,
  selectedTuteeId,
  onSelect,
  variant = 'default',
}: AdminGroupSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const activeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(false);

  const filteredTutees = useMemo(() => {
    if (!searchTerm.trim()) return tutees;
    return tutees.filter((t) => t.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [tutees, searchTerm]);

  const getIcon = (iconName: string) => iconMap[iconName] || GraduationCap;
  const isSticky = variant === 'sticky';

  const updateScrollFades = () => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setFadeLeft(overflow && el.scrollLeft > 2);
    setFadeRight(overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    updateScrollFades();
    const el = scrollRef.current;
    if (!el) return;

    const ro = new ResizeObserver(updateScrollFades);
    ro.observe(el);
    return () => ro.disconnect();
  }, [filteredTutees.length, variant]);

  useEffect(() => {
    if (!activeRef.current) return;
    activeRef.current.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    requestAnimationFrame(updateScrollFades);
  }, [selectedTuteeId, filteredTutees.length]);

  if (tutees.length === 0) return null;

  const fadeFrom = isSticky ? 'from-white/95 via-white/70' : 'from-white via-white/80';

  return (
    <div className="flex flex-col gap-2">
      {tutees.length > 5 && (
        <div className="relative shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={
              isSticky
                ? 'w-full pl-9 pr-3 py-2 text-[13px] bg-black/[0.04] border-0 rounded-xl focus:ring-2 focus:ring-indigo-500/40 outline-none'
                : 'w-full pl-9 pr-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none'
            }
          />
        </div>
      )}

      <ScrollFadeRow
        scrollRef={scrollRef}
        fadeLeft={fadeLeft}
        fadeRight={fadeRight}
        onScroll={updateScrollFades}
        fadeFromClass={fadeFrom}
      >
        {filteredTutees.map((tutee) => {
          const isActive = selectedTuteeId === tutee.id;

          if (isSticky) {
            return (
              <button
                key={tutee.id}
                ref={isActive ? activeRef : undefined}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onSelect(tutee.id)}
                className={`flex-shrink-0 snap-center px-4 py-1.5 rounded-full text-[13px] font-medium whitespace-nowrap transition-all touch-manipulation active:scale-[0.97] ${
                  isActive
                    ? `bg-gradient-to-br ${tutee.colorScheme.gradient} text-white shadow-sm`
                    : 'bg-black/[0.06] text-gray-700 hover:bg-black/[0.09]'
                }`}
              >
                {tutee.name}
              </button>
            );
          }

          const IconComp = getIcon(tutee.icon);
          return (
            <button
              key={tutee.id}
              ref={isActive ? activeRef : undefined}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(tutee.id)}
              className={`group flex-shrink-0 snap-center flex flex-col items-center w-[4.5rem] sm:w-[5rem] rounded-xl transition-all duration-200 touch-manipulation p-2 min-h-[68px] ${
                isActive
                  ? `bg-gradient-to-br ${tutee.colorScheme.gradient} text-white shadow-md`
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100'
              }`}
            >
              <div
                className={`mb-1 p-1.5 rounded-lg transition-colors ${
                  isActive ? 'bg-white/20' : 'bg-white group-hover:bg-gray-50'
                }`}
              >
                <IconComp
                  className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-indigo-500'}`}
                />
              </div>
              <span
                className={`text-[9px] sm:text-[10px] font-bold text-center line-clamp-2 leading-tight w-full ${
                  isActive ? 'text-white' : 'text-gray-600'
                }`}
              >
                {tutee.name}
              </span>
            </button>
          );
        })}
      </ScrollFadeRow>
    </div>
  );
};

export default AdminGroupSelector;
